import React from 'react';
import PropTypes from 'prop-types';
import { Notification, Typography } from '@telus-uds/components-web';

import RichText from '../richText';

const isBlankText = node =>
  node.nodeType === 'text' && /^\n*$/.test(node.value || '');

// Stray line breaks pasted into Contentful (e.g. the French prequalification note
// ends with "\n\n\n") are rendered literally and leave a large blank gap inside the
// note box. Strip them from the edges of each paragraph and drop empty paragraphs.
const trimParagraphEdges = data => {
  if (!data || !Array.isArray(data.content)) return data;

  const content = data.content
    .map(node => {
      if (node.nodeType !== 'paragraph' || !Array.isArray(node.content)) {
        return node;
      }
      const children = [...node.content];
      while (children.length && isBlankText(children[0])) children.shift();
      while (children.length && isBlankText(children[children.length - 1])) {
        children.pop();
      }
      if (!children.length) return null;

      const first = children[0];
      if (first.nodeType === 'text') {
        children[0] = { ...first, value: first.value.replace(/^\n+/, '') };
      }
      const lastIndex = children.length - 1;
      const last = children[lastIndex];
      if (last.nodeType === 'text') {
        children[lastIndex] = { ...last, value: last.value.replace(/\n+$/, '') };
      }
      return { ...node, content: children };
    })
    .filter(Boolean);

  return { ...data, content };
};

const Note = ({ 
  type = 'text', 
  variant = '', 
  text = '', 
  data, 
  options = { renderMark: {}, renderNode: {} },
  isShowNoteBackground = true,
  isWarningMessage = false
}) => {
  // Map TDS variant to UDS variant
  let udsVariant = {};
  if (variant && variant !== 'note') {
    if (variant === 'error') {
      udsVariant = { validation: 'error' };
    } else if (variant === 'success') {
      udsVariant = { style: 'success' };
    } else if (variant === 'warning') {
      udsVariant = { style: 'warning' };
    } else if (variant === 'info') {
      udsVariant = { style: 'subtle' };
    }
  }
  
  const content = type === 'richText' ? (
    <Typography>
      <RichText data={trimParagraphEdges(data)} options={options} />
    </Typography>
  ) : (
    <Typography>
      <p>{text}</p>
    </Typography>
  );
  
  return (
    <div style={{ margin: '1em 0' }}>
      {isShowNoteBackground ? (
        <Notification variant={udsVariant}>
          {content}
        </Notification>
      ) : (
        content
      )}
    </div>
  );
};

Note.propTypes = {
  type: PropTypes.string,
  text: PropTypes.string,
  variant: PropTypes.string,
  data: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.shape({})).isRequired
  }).isRequired,
  options: PropTypes.shape({
    renderMark: PropTypes.shape({}),
    renderNode: PropTypes.shape({})
  }),
  isShowNoteBackground: PropTypes.bool,
  isWarningMessage: PropTypes.bool
};

export default Note;
